// Wishlist
(function () {
const ThemeUtils = window.DutchXXLTheme || {};

const formatMoney = (value) => {
  const cents = Number.parseInt(value, 10) || 0;
  if (typeof ThemeUtils.formatMoney === "function") return ThemeUtils.formatMoney(cents);

  try {
    return new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: "EUR",
    }).format(cents / 100);
  } catch {
    return `€${(cents / 100).toFixed(2).replace(".", ",")}`;
  }
};

const cartDrawerSectionId = () =>
  document.querySelector("[data-cart-drawer]")?.dataset.cartSectionId || "";

const currentSectionsUrl = () =>
  `${window.location.pathname}${window.location.search}` || "/";

const storefrontRoot = () => {
  const root = window.Shopify?.routes?.root || "/";
  return root.replace(/\/?$/, "/");
};

class Wishlist {
  static STORAGE_KEY = "dutchxxl_wishlist";
  static MAX_ITEMS = 60;

  constructor() {
    this.items = this.loadFromStorage();
    this.productCache = new Map();
    this.hydratingHandles = new Set();
    this.hydrationFailedHandles = new Set();
    this.initToggleButtons();
    this.initWishlistPage();
    this.syncState();

    document.addEventListener("wishlist:refresh", () => this.syncState());
    window.addEventListener("storage", (event) => {
      if (event.key !== Wishlist.STORAGE_KEY && event.key !== null) return;
      this.items = this.loadFromStorage();
      this.syncState();
      this.renderWishlistPage();
    });
    window.DutchXXLWishlist = this;
  }

  loadFromStorage() {
    try {
      const data = window.localStorage.getItem(Wishlist.STORAGE_KEY);
      return data ? this.normalizeItems(JSON.parse(data)) : {};
    } catch {
      return {};
    }
  }

  saveToStorage() {
    try {
      window.localStorage.setItem(Wishlist.STORAGE_KEY, JSON.stringify(this.items));
    } catch {}
    this.syncState();
    this.dispatchChange();
  }

  normalizeItems(data) {
    const normalized = {};
    if (!data || typeof data !== "object") return normalized;

    const entries = Array.isArray(data)
      ? data.map((item) => [item?.handle, item])
      : Object.entries(data);

    for (const [key, value] of entries) {
      if (!value || typeof value !== "object") continue;
      const handle = this.sanitizeHandle(value.handle || key);
      if (!handle || normalized[handle]) continue;

      const item = this.normalizeItem(handle, value);
      if (!item) continue;

      normalized[handle] = item;
      if (Object.keys(normalized).length >= Wishlist.MAX_ITEMS) break;
    }

    return normalized;
  }

  normalizeItem(handle, data) {
    const title = this.sanitizeText(data.title, 180);
    if (!handle || !title) return null;

    let url = this.sanitizeProductUrl(data.url, handle);
    if (!url) return null;

    const variantId = this.sanitizeId(
      data.variantId || data.variant_id || this.variantIdFromUrl(url),
    );
    url = this.appendVariantToUrl(url, variantId);

    const image = this.sanitizeImageUrl(data.image);
    if (data.image && !image) return null;

    const variantAvailability = this.parseBoolean(data.variantAvailable ?? data.available);

    return {
      handle,
      title,
      variant: this.cleanVariantTitle(data.variant || data.variantTitle || data.variant_title || ""),
      variantId,
      variantAvailable: variantAvailability === false ? false : true,
      url,
      image,
      price: this.sanitizeCents(data.price),
      comparePrice: this.sanitizeCents(data.comparePrice ?? data.compare_at_price),
      addedAt: this.sanitizeTimestamp(data.addedAt),
    };
  }

  sanitizeHandle(value) {
    const handle = String(value || "").trim().toLowerCase();
    return /^[a-z0-9][a-z0-9-]{0,254}$/.test(handle) ? handle : "";
  }

  sanitizeId(value) {
    const id = String(value || "").trim();
    return /^\d{1,20}$/.test(id) ? id : "";
  }

  sanitizeText(value, maxLength = 180) {
    return String(value || "")
      .replace(/[\u0000-\u001F\u007F]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, maxLength);
  }

  cleanVariantTitle(value) {
    const title = this.sanitizeText(value, 120);
    return title && title !== "Default Title" ? title : "";
  }

  cleanVariantOptionText(value) {
    return this.cleanVariantTitle(String(value || "").split(" – ")[0]);
  }

  sanitizeCents(value) {
    const cents = Number.parseInt(value, 10);
    if (!Number.isFinite(cents) || cents < 0) return 0;
    return Math.min(cents, 100000000);
  }

  sanitizeTimestamp(value) {
    const timestamp = Number(value);
    const now = Date.now();
    if (!Number.isFinite(timestamp) || timestamp <= 0) return now;
    return Math.min(timestamp, now + 86400000);
  }

  parseBoolean(value) {
    if (value === true || value === "true") return true;
    if (value === false || value === "false") return false;
    return null;
  }

  sanitizeProductUrl(value, handle) {
    const fallback = `/products/${handle}`;
    const raw = String(value || "").trim();
    if (!raw) return fallback;

    try {
      const url = new URL(raw, window.location.origin);
      if (url.origin !== window.location.origin) return "";
      if (!url.pathname.startsWith("/products/")) return "";
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return "";
    }
  }

  appendVariantToUrl(value, variantId) {
    const safeVariantId = this.sanitizeId(variantId);
    if (!safeVariantId) return value;

    try {
      const url = new URL(value, window.location.origin);
      url.searchParams.set("variant", safeVariantId);
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return value;
    }
  }

  variantIdFromUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      return this.sanitizeId(url.searchParams.get("variant"));
    } catch {
      return "";
    }
  }

  sanitizeImageUrl(value) {
    const raw = String(value || "").trim();
    if (!raw) return "";

    try {
      const url = new URL(raw, window.location.href);
      const host = url.hostname.toLowerCase();
      const isAllowedProtocol = url.protocol === "https:" || url.protocol === "http:";
      const isAllowedHost =
        url.origin === window.location.origin ||
        host === "cdn.shopify.com" ||
        host.endsWith(".shopifycdn.net") ||
        host.endsWith(".myshopify.com");

      return isAllowedProtocol && isAllowedHost ? url.href : "";
    } catch {
      return "";
    }
  }

  add(handle, data) {
    const safeHandle = this.sanitizeHandle(handle);
    if (!safeHandle || this.items[safeHandle]) return false;

    const item = this.normalizeItem(safeHandle, { ...data, addedAt: Date.now() });
    if (!item) return false;

    this.items[safeHandle] = item;
    this.saveToStorage();
    return true;
  }

  remove(handle) {
    const safeHandle = this.sanitizeHandle(handle);
    if (!safeHandle || !this.items[safeHandle]) return false;

    delete this.items[safeHandle];
    this.saveToStorage();
    return true;
  }

  toggle(handle, data) {
    if (this.has(handle)) {
      this.remove(handle);
      return false;
    }
    this.add(handle, data);
    return true;
  }

  has(handle) {
    const safeHandle = this.sanitizeHandle(handle);
    return !!(safeHandle && this.items[safeHandle]);
  }

  updateHeaderCount() {
    const count = Object.keys(this.items).length;
    ThemeUtils.syncWishlistCount?.(count);
  }

  getSelectedVariantData(button) {
    const form = button.closest("form");
    const field = form?.querySelector('[name="id"]');
    const selected = field?.matches("select") ? field.selectedOptions?.[0] : null;
    const id = this.sanitizeId(selected?.value || field?.value || button.dataset.productVariantId);
    const available = selected
      ? !selected.disabled && this.parseBoolean(selected.dataset.variantAvailable) !== false
      : this.parseBoolean(button.dataset.productVariantAvailable) !== false;

    return {
      id,
      title: this.cleanVariantTitle(
        selected?.dataset.variantTitle ||
          button.dataset.productVariantTitle ||
          this.cleanVariantOptionText(selected?.textContent || ""),
      ),
      image: selected?.dataset.variantImage || button.dataset.productImage || "",
      price: selected?.dataset.variantPrice || button.dataset.productPrice || 0,
      comparePrice: selected?.dataset.variantComparePrice || button.dataset.productComparePrice || 0,
      available,
    };
  }

  initToggleButtons() {
    document.addEventListener("click", (event) => {
      const btn = event.target.closest("[data-wishlist-toggle]");
      if (!btn) return;

      event.preventDefault();
      event.stopPropagation();

      const handle = btn.dataset.wishlistToggle;
      const variant = this.getSelectedVariantData(btn);
      this.toggle(handle, {
        title: btn.dataset.productTitle || "",
        variant: variant.title,
        variantId: variant.id,
        variantAvailable: variant.available,
        url: this.appendVariantToUrl(btn.dataset.productUrl || "", variant.id),
        image: variant.image,
        price: parseInt(variant.price, 10) || 0,
        comparePrice: parseInt(variant.comparePrice, 10) || 0,
      });
    });
  }

  syncState() {
    this.updateHeaderCount();
    this.updateToggleButtons();
  }

  dispatchChange() {
    try {
      document.dispatchEvent(
        new CustomEvent("wishlist:changed", {
          detail: { count: Object.keys(this.items).length },
        }),
      );
    } catch {}
  }

  updateToggleButtons() {
    document
      .querySelectorAll("[data-wishlist-toggle]")
      .forEach((element) => {
        const active = this.has(element.dataset.wishlistToggle);
        element.classList.toggle("is-wishlisted", active);
        element.setAttribute("aria-pressed", active ? "true" : "false");
        element.setAttribute(
          "aria-label",
          active ? "Verwijderen uit favorieten" : "Toevoegen aan favorieten",
        );
      });
  }

  initWishlistPage() {
    const section = document.querySelector("[data-wishlist-section]");
    if (!section) return;

    this.pageGrid = section.querySelector("[data-wishlist-grid]");
    this.pageEmpty = section.querySelector("[data-wishlist-empty]");
    this.pageStatus = section.querySelector("[data-wishlist-status]");

    this.pageGrid?.addEventListener("click", (event) => {
      const removeButton = event.target.closest("[data-wishlist-remove]");
      if (removeButton) {
        event.preventDefault();
        this.remove(removeButton.dataset.wishlistRemove);
        this.renderWishlistPage();
        this.setStatus("Product verwijderd uit je verlanglijst.");
        return;
      }

      const addButton = event.target.closest("[data-wishlist-add-to-cart]");
      if (addButton) {
        event.preventDefault();
        this.moveToCart(addButton.dataset.wishlistAddToCart, addButton);
      }
    });

    this.renderWishlistPage();
  }

  renderWishlistPage() {
    if (!this.pageGrid) return;

    const items = Object.values(this.items).sort((a, b) => b.addedAt - a.addedAt);
    if (!items.length) {
      if (this.pageEmpty) this.pageEmpty.hidden = false;
      this.pageGrid.hidden = true;
      this.pageGrid.replaceChildren();
      return;
    }

    if (this.pageEmpty) this.pageEmpty.hidden = true;
    this.pageGrid.hidden = false;

    const fragment = document.createDocumentFragment();
    items.forEach((item) => fragment.appendChild(this.createItemElement(item)));
    this.pageGrid.replaceChildren(fragment);
    this.hydrateMissingVariants(items);
  }

  createItemElement(item) {
    const card = document.createElement("article");
    const titleText = item.title || "Product";
    card.className = "wishlist-card";
    card.dataset.handle = item.handle;
    card.setAttribute("aria-busy", "false");
    card.setAttribute("role", "listitem");

    const removeButton = document.createElement("button");
    removeButton.className = "wishlist-card__remove";
    removeButton.dataset.wishlistRemove = item.handle;
    removeButton.type = "button";
    removeButton.setAttribute("aria-label", `${titleText} verwijderen uit verlanglijst`);
    removeButton.textContent = "×";

    const imageLink = document.createElement("a");
    imageLink.className = "wishlist-card__media";
    imageLink.href = item.url;
    imageLink.setAttribute("aria-label", titleText);

    if (item.image) {
      const image = document.createElement("img");
      image.className = "wishlist-card__image";
      image.src = item.image;
      image.alt = titleText;
      image.loading = "lazy";
      image.width = 240;
      image.height = 360;
      imageLink.append(image);
    } else {
      const placeholder = document.createElement("span");
      placeholder.className = "wishlist-card__image-placeholder";
      placeholder.textContent = "Geen afbeelding";
      imageLink.append(placeholder);
    }

    const body = document.createElement("div");
    body.className = "wishlist-card__body";

    const details = document.createElement("div");
    details.className = "wishlist-card__details";

    const title = document.createElement("h2");
    title.className = "wishlist-card__title";
    const titleLink = document.createElement("a");
    titleLink.className = "wishlist-card__name";
    titleLink.href = item.url;
    titleLink.textContent = titleText;
    title.append(titleLink);
    details.append(title);

    if (item.variant) {
      const variant = document.createElement("span");
      variant.className = "wishlist-card__variant";
      variant.textContent = item.variant;
      details.append(variant);
    }

    const priceWrapper = document.createElement("div");
    priceWrapper.className = "wishlist-card__price";

    if (item.comparePrice > item.price) {
      const comparePrice = document.createElement("span");
      comparePrice.className = "wishlist-card__price-was";
      comparePrice.textContent = formatMoney(item.comparePrice);
      priceWrapper.append(comparePrice);
    }

    const price = document.createElement("span");
    price.className = "wishlist-card__price-now";
    price.textContent = formatMoney(item.price);
    priceWrapper.append(price);

    const actions = document.createElement("div");
    actions.className = "wishlist-card__actions";

    const addButton = document.createElement("button");
    addButton.className = "wishlist-card__add";
    addButton.dataset.wishlistAddToCart = item.handle;
    addButton.type = "button";
    addButton.setAttribute("aria-busy", "false");
    addButton.textContent = item.variantAvailable === false ? "Niet op voorraad" : "Toevoegen";
    addButton.disabled = item.variantAvailable === false;
    addButton.setAttribute(
      "aria-label",
      item.variantAvailable === false
        ? `${titleText} is niet op voorraad`
        : `Voeg ${titleText} toe aan winkelwagen`,
    );

    actions.append(addButton);
    body.append(details, priceWrapper, actions);
    card.append(removeButton, imageLink, body);

    return card;
  }

  async hydrateMissingVariants(items) {
    const missing = items.filter(
      (item) =>
        !item.variantId &&
        !this.hydratingHandles.has(item.handle) &&
        !this.hydrationFailedHandles.has(item.handle),
    );
    if (!missing.length) return;

    let changed = false;
    await Promise.all(
      missing.map(async (item) => {
        const updated = await this.hydrateItemVariant(item, false);
        if (updated?.variantId) changed = true;
      }),
    );

    if (changed) {
      this.saveToStorage();
      this.renderWishlistPage();
    }
  }

  async hydrateItemVariant(item, shouldSave = true) {
    const safeHandle = this.sanitizeHandle(item?.handle);
    if (!safeHandle) return item;

    this.hydratingHandles.add(safeHandle);
    try {
      const product = await this.fetchProduct(safeHandle);
      const variant = this.findVariantForItem(product, item);
      if (!variant) {
        this.hydrationFailedHandles.add(safeHandle);
        return item;
      }

      const variantTitle = this.cleanVariantTitle(variant.title);
      const image = this.pickProductImage(product, variant, item.image);
      const productUrl = this.sanitizeProductUrl(product.url || item.url, safeHandle);
      const updated = this.normalizeItem(safeHandle, {
        ...item,
        title: product.title || item.title,
        variant: variantTitle,
        variantId: variant.id,
        variantAvailable: variant.available,
        url: productUrl,
        image,
        price: variant.price,
        comparePrice: variant.compare_at_price,
      });

      if (!updated) return item;
      this.items[safeHandle] = updated;
      if (shouldSave) this.saveToStorage();
      return updated;
    } catch {
      this.hydrationFailedHandles.add(safeHandle);
      return item;
    } finally {
      this.hydratingHandles.delete(safeHandle);
    }
  }

  async fetchProduct(handle) {
    if (this.productCache.has(handle)) return this.productCache.get(handle);

    const response = await fetch(`${storefrontRoot()}products/${handle}.js`, {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error("Productgegevens konden niet worden opgehaald.");

    const product = await response.json();
    this.productCache.set(handle, product);
    return product;
  }

  findVariantForItem(product, item) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    if (!variants.length) return null;

    const variantId = this.sanitizeId(item.variantId || this.variantIdFromUrl(item.url));
    if (variantId) {
      const selectedVariant = variants.find((variant) => String(variant.id) === variantId);
      if (selectedVariant) return selectedVariant;
    }

    return variants.find((variant) => variant.available) || variants[0];
  }

  pickProductImage(product, variant, fallback) {
    const variantImage = variant?.featured_image?.src || variant?.featured_image || variant?.image?.src || variant?.image;
    const productImage = product?.featured_image || product?.images?.[0];
    return variantImage || productImage || fallback || "";
  }

  async moveToCart(handle, button) {
    const safeHandle = this.sanitizeHandle(handle);
    let item = this.items[safeHandle];
    if (!item) return;

    const originalText = button.textContent;
    this.setButtonBusy(button, true, "Toevoegen...");

    try {
      if (!item.variantId) item = await this.hydrateItemVariant(item);
      if (!item?.variantId) throw new Error("Kon deze maat niet toevoegen. Bekijk het product en probeer opnieuw.");
      if (item.variantAvailable === false) throw new Error("Deze maat is niet op voorraad.");

      const formData = new FormData();
      formData.append("id", item.variantId);
      formData.append("quantity", "1");

      const sectionId = cartDrawerSectionId();
      if (sectionId) {
        formData.append("sections", sectionId);
        formData.append("sections_url", currentSectionsUrl());
      }

      const response = await fetch(`${storefrontRoot()}cart/add.js`, {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });
      const data = await this.parseAddResponse(response);

      this.remove(item.handle);
      this.renderWishlistPage();
      this.setStatus(`${item.title} is toegevoegd aan je winkelwagen.`);
      window.dispatchEvent(new CustomEvent("cart:item-added", { detail: data }));
      window.dispatchEvent(new CustomEvent("cart:open-drawer"));
    } catch (error) {
      this.setStatus(error?.message || "Kon product niet toevoegen. Probeer het opnieuw.");
      if (button.isConnected) this.setButtonBusy(button, false, originalText);
    }
  }

  async parseAddResponse(response) {
    const text = await response.text();
    let data = {};

    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        if (!response.ok) {
          throw new Error("Kon product niet toevoegen. Probeer het opnieuw.");
        }
      }
    }

    if (!response.ok) {
      throw new Error(
        data.description ||
          data.message ||
          "Kon product niet toevoegen. Probeer het opnieuw.",
      );
    }

    return data;
  }

  setButtonBusy(button, busy, text) {
    if (!button) return;
    button.setAttribute("aria-busy", busy ? "true" : "false");
    button.disabled = busy;
    if (text) button.textContent = text;
  }

  setStatus(message) {
    if (!this.pageStatus) return;
    this.pageStatus.textContent = "";
    window.requestAnimationFrame(() => {
      this.pageStatus.textContent = message;
    });
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => new Wishlist());
} else {
  new Wishlist();
}
})();
